class FurlioWeeklyDropPromo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.content = this.querySelector('[data-promo-content]');
    if (!this.content) { this.initialized = false; return; }
    this.badge = this.querySelector('[data-promo-badge]');
    this.fallback ??= this.content.cloneNode(true);
    this.contentObserver = new MutationObserver(() => this.renderPreview());
    this.contentObserver.observe(this, { childList: true, subtree: true });
    this.onVisibility = () => {
      if (document.hidden) return;
      this.renderPreview();
      this.refresh();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
    this.refresh();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.contentObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.initialized = false;
  }

  async refresh() {
    if (!this.isConnected || this.loading || document.hidden) return;
    this.renderPreview();
    this.scheduleRefresh();
    this.loading = true;
    const controller = new AbortController();
    this.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const source = new URL(this.dataset.source, window.location.origin);
      if (source.origin !== window.location.origin) throw new Error('Invalid Weekly Drop source');
      const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewTheme) source.searchParams.set('preview_theme_id', previewTheme);
      const response = await fetch(source, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Weekly Drop unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const preview = page.querySelector('template[data-weekly-drop-preview]');
      if (!preview) throw new Error('Weekly Drop preview unavailable');
      if (!this.isConnected || controller.signal.aborted) return;
      this.preview = preview;
    } catch {
      if (!this.isConnected) return;
    } finally {
      clearTimeout(timeout);
      this.loading = false;
      if (this.isConnected) {
        this.renderPreview();
        this.scheduleRefresh();
      }
    }
  }

  renderPreview() {
    if (!this.preview) return;
    this.content = this.querySelector('[data-promo-content]');
    this.badge = this.querySelector('[data-promo-badge]');
    if (!this.content) return;
    const start = Date.parse(this.preview.dataset.start);
    const end = Date.parse(this.preview.dataset.end);
    const now = Date.now();
    const valid = Number.isFinite(start) && Number.isFinite(end) && end > start;
    const timedOut = this.preview.dataset.state === 'active' && (!valid || now < start || now >= end);
    if (this.dataset.mode === 'announcement') {
      const message = timedOut
        ? (valid && now >= end ? this.dataset.ended : this.dataset.coming)
        : this.preview.dataset.announcement;
      const text = message || this.fallback.textContent.trim();
      if (this.content.textContent !== text) this.content.textContent = text;
      const label = this.querySelector('[data-announcement-label]');
      if (label && label.textContent !== text) label.textContent = text;
      return;
    }
    if (!this.badge) return;
    const signature = timedOut ? `inactive:${now >= end}` : this.preview.outerHTML;
    const badge = timedOut ? this.dataset.idleBadge : this.preview.dataset.badge || this.dataset.idleBadge;
    if (signature === this.signature && this.content.innerHTML === this.renderedContent && this.badge.textContent === badge) return;
    this.signature = signature;
    const hadFocus = this.content.contains(document.activeElement);
    if (timedOut) {
      const fallback = this.fallback.cloneNode(true);
      fallback.querySelector('p').textContent = valid && now >= end ? this.dataset.ended : this.dataset.coming;
      this.content.replaceChildren(...fallback.childNodes);
      this.badge.textContent = this.dataset.idleBadge;
    } else {
      this.content.replaceChildren(document.importNode(this.preview.content, true));
      this.badge.textContent = this.preview.dataset.badge || this.dataset.idleBadge;
    }
    this.renderedContent = this.content.innerHTML;
    if (hadFocus) this.content.querySelector('a')?.focus({ preventScroll: true });
  }

  scheduleRefresh() {
    clearTimeout(this.timer);
    const now = Date.now();
    const milestones = this.preview
      ? [this.preview.dataset.start, this.preview.dataset.end, this.preview.dataset.nextStart]
        .map(value => Date.parse(value)).filter(time => Number.isFinite(time) && time > now)
      : [];
    const delay = Math.min(300000, ...milestones.map(time => time - now));
    this.timer = setTimeout(() => {
      this.renderPreview();
      this.refresh();
    }, Math.max(100, delay));
  }
}

if (!customElements.get('furlio-weekly-drop-promo')) {
  customElements.define('furlio-weekly-drop-promo', FurlioWeeklyDropPromo);
}
